import { pick, omit } from 'lodash-es';

import {
  AVAILABILITY_STATUSES,
  RESERVATIONS_TYPES,
  FILTER_RULE_ENUMS,
  FILTER_OPERATOR_ENUMS,
} from '@constants';
import { formatDate, formatVehicleStage } from '@utils';
import { AVAILABILITY_PAGE_SIZE } from '@store';

import {
  DEFAULT_LOCATIONS,
  DEFLEETED_PRIORITIES,
} from 'hooks/vehicles/constants';

import { AVAILABILITY_TABLE_TABS } from 'components/availabilityTable/AvailabilityTableFilters/constants';
import { WWW_AVAILABILITY } from './constants';

export const getAvailabilityQueryParams = (payload = {}) => {
  if (!payload?.pageSize) {
    payload.pageSize = AVAILABILITY_PAGE_SIZE;
  }
  if (!payload?.page) {
    payload.page = 1;
  }

  return pick(payload, [
    'page',
    'pageSize',
    'order',
    'orderBy',
    'tab',
    'query',
  ]);
};

// for https://linear.app/instacar/issue/INS-1996/[instafleet]-availability-improvementsfixes
// returns the rule for cars with:
// a. "Standard" or "Closed Contract" subscription type (status = ΟnLease || OnLeaseARM || ForReturn) AND
// b. Subscription status = Active (Reservation != predel/temp)

export const getAvailabilityRequestPayload = (
  payload = {},
  modalFiltersRules = null,
  enums = {}
) => {
  const fields =
    omit(payload, ['page', 'pageSize', 'order', 'orderBy', 'query', 'tab']) ||
    {};
  const numberFields = [];
  const multiFilterStringFields = [
    'location',
    'brand',
    'model',
    'color',
    'sourcingType',
    'categoryClass',
    'condition',
  ];
  const multiFilterNumberFields = [
    'status',
    'reservationType',
    'procurementStageId',
    'engineType',
    'fuelType',
    'bodyType',
    'transmission',
    'skuType',
  ];
  const fieldsMap = {
    reservationType: 'availability.reservationType',
    status: 'availability.status',
    location: 'location.name',
    color: 'properties.color',
    procurementStageId: 'procurement.currentStageId',
    sourcingType: 'procurement.sourcingType',
    brand: 'sku.properties.brand',
    model: 'sku.properties.model',
    skuType: 'sku.type',
    transmission: 'sku.properties.transmission',
    bodyType: 'sku.properties.body.type',
    fuelType: 'sku.properties.engine.fuel_type',
    engineType: 'sku.properties.engine.type',
    categoryClass: 'sku.properties.categoryClass',
    condition: 'condition',
  };
  const { tab } = payload;

  const isPredelTab = tab === AVAILABILITY_TABLE_TABS.predel.key;

  const isIdleTab = tab === AVAILABILITY_TABLE_TABS.idle.key;

  const isSalesTab = tab === AVAILABILITY_TABLE_TABS.sales.key;

  const _getValue = ({ field }) => {
    if (multiFilterStringFields.includes(field)) {
      return payload[field]?.split(',');
    }
    if (multiFilterNumberFields.includes(field)) {
      return payload[field]?.split(',').map((item) => Number(item));
    }
    return numberFields.includes(field)
      ? Number(payload[field])
      : payload[field];
  };
  const _getType = ({ field }) => {
    if (
      multiFilterStringFields.includes(field) ||
      multiFilterNumberFields.includes(field)
    ) {
      return FILTER_RULE_ENUMS.IsAnyOfRuleType;
    }
    return FILTER_RULE_ENUMS.ComparableRuleType;
  };

  const value = [];

  if (!isPredelTab && !isIdleTab) {
    value.push(
      ...Object.keys(fields).map((field) => ({
        field: fieldsMap[field] || field,
        operator: FILTER_OPERATOR_ENUMS['='],
        type: _getType({ field }),
        value: _getValue({ field }),
      }))
    );

    value.push(...(modalFiltersRules?.value || []));
  }

  const reservationsTypes = enums?.reservationsTypes?.reduce(
    (acc, reservation) => {
      Object.assign(acc, { [reservation.text]: reservation.value });
      return acc;
    },
    {}
  );

  const statusTypes = enums?.statuses?.reduce((acc, status) => {
    Object.assign(acc, { [status.text]: status.value });
    return acc;
  }, {});

  const procurementStages = enums?.procurementStages?.reduce((acc, stage) => {
    Object.assign(acc, { [stage.text]: stage.value });
    return acc;
  }, {});

  const skuTypes = enums?.skuTypes?.reduce((acc, skuType) => {
    Object.assign(acc, { [skuType.text]: skuType.value });
    return acc;
  }, {});

  if (isPredelTab) {
    // Hard filters for the "predel" tab

    value.push(
      {
        field: 'availability.reservationType',
        operator: FILTER_OPERATOR_ENUMS['='],
        type: FILTER_RULE_ENUMS.IsAnyOfRuleType,
        value: [reservationsTypes.Free],
      },
      {
        field: 'availability.status',
        operator: FILTER_OPERATOR_ENUMS['='],
        type: FILTER_RULE_ENUMS.IsAnyOfRuleType,
        value: [statusTypes.Available],
      },
      {
        field: 'procurement.currentStageId',
        operator: FILTER_OPERATOR_ENUMS['='],
        type: FILTER_RULE_ENUMS.IsAnyOfRuleType,
        value: [procurementStages.Fleet],
      },
      {
        field: 'sku.type',
        operator: FILTER_OPERATOR_ENUMS['='],
        type: FILTER_RULE_ENUMS.IsAnyOfRuleType,
        value: [skuTypes.car],
      }
    );
  }

  if (isIdleTab) {
    // Hard filters for the "idle" tab

    value.push(
      {
        field: 'availability.reservationType',
        operator: FILTER_OPERATOR_ENUMS['='],
        type: FILTER_RULE_ENUMS.IsAnyOfRuleType,
        value: [
          reservationsTypes.Free,
          reservationsTypes['On Hold'],
          reservationsTypes['For Replace'],
          reservationsTypes['Seasonal Cars'],
        ],
      },
      {
        field: 'availability.status',
        operator: FILTER_OPERATOR_ENUMS['='],
        type: FILTER_RULE_ENUMS.IsAnyOfRuleType,
        value: [
          statusTypes.ARM,
          statusTypes['Vehicle Inspection Pending'],
          statusTypes.Available,
        ],
      },
      {
        field: 'procurement.currentStageId',
        operator: FILTER_OPERATOR_ENUMS['='],
        type: FILTER_RULE_ENUMS.IsAnyOfRuleType,
        value: [procurementStages.Fleet],
      },
      {
        field: 'sku.type',
        operator: FILTER_OPERATOR_ENUMS['='],
        type: FILTER_RULE_ENUMS.IsAnyOfRuleType,
        value: [skuTypes.car, skuTypes.van],
      }
    );
  }

  if (isSalesTab) {
    // Hard filters for the "sales" tab

    value.push(
      {
        field: 'availability.reservationType',
        operator: FILTER_OPERATOR_ENUMS['='],
        type: FILTER_RULE_ENUMS.IsAnyOfRuleType,
        value: [reservationsTypes.Free, reservationsTypes['On Hold']],
      },
      {
        field: 'availability.status',
        operator: FILTER_OPERATOR_ENUMS['='],
        type: FILTER_RULE_ENUMS.IsAnyOfRuleType,
        value: [statusTypes.Available],
      },
      {
        field: 'procurement.currentStageId',
        operator: FILTER_OPERATOR_ENUMS['='],
        type: FILTER_RULE_ENUMS.IsAnyOfRuleType,
        value: [procurementStages.Fleet],
      }
    );
  }

  if (value.length) {
    return {
      type: FILTER_RULE_ENUMS.AndRuleType,
      field: null,
      operator: null,
      value,
    };
  }

  return {
    type: FILTER_RULE_ENUMS.EmptyRuleType,
  };
};

const normalizeConstantsToMap = (data) =>
  Object.keys(data).reduce((acc, cur) => {
    acc = {
      ...acc,
      [data[cur].value]: data[cur].text,
    };

    return acc;
  }, {});

const getReservationType = (item, reservationTypes) => {
  if (
    item.reservationType >= RESERVATIONS_TYPES.free.value &&
    reservationTypes[item.reservationType]
  ) {
    return reservationTypes[item.reservationType];
  }

  return '-';
};

export const handleAvailability = (data = []) => {
  if (!data || !data.length) {
    return [];
  }

  const statuses = normalizeConstantsToMap(AVAILABILITY_STATUSES);
  const reservationTypes = normalizeConstantsToMap(RESERVATIONS_TYPES);
  const priorityMap = DEFLEETED_PRIORITIES?.reduce((acc, cur) => {
    acc[cur.value] = cur;
    return acc;
  }, {});
  return data.map((item) => {
    let erfuddValue = '-';
    if (item.erfudd) {
      const erfuddDate = new Date(item.erfudd);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (erfuddDate < today) {
        erfuddValue = {
          label: 'AVAILABLE NOW',
          chip: true,
        };
      } else {
        erfuddValue = formatDate(item.erfudd, 'dd/MM/yyyy');
      }
    }
    const pricingMap = item.pricing?.reduce((acc, cur) => {
      if (cur.enabled) {
        acc[cur.monthsDuration || 'month-to-month'] = cur.price?.number || '-';
      }
      return acc;
    }, {});

    return {
      ...item,
      skuId: item.skuId || '-',
      plate: item.plate || '-',
      brand: item.brand || '-',
      model: item.model || '-',
      edition: item.edition || '-',
      fuelType: item.fuelType || '-',
      wwwAvailability:
        WWW_AVAILABILITY[item.wwwAvailability] || item.wwwAvailability || '-',
      price: pricingMap?.['month-to-month'] ?? '-',
      price12: pricingMap?.[12] ?? '-',
      price24: pricingMap?.[24] ?? '-',
      price36: pricingMap?.[36] ?? '-',
      vin: item.vin || '-',
      erfudd: erfuddValue,
      id: item.vehicleId,
      procurementStageId: formatVehicleStage(item.procurementStageId),
      status:
        item.status >= AVAILABILITY_STATUSES.onlease.value
          ? statuses[item.status]
          : '-',
      vehicleMileageFromOdometer: item.vehicleMileageFromOdometer
        ? item.vehicleMileageFromOdometer.toLocaleString('de-DE')
        : '-',
      reservationType: getReservationType(item, reservationTypes),
      returnDate: item.returnDate
        ? formatDate(item.returnDate, 'dd/MM/yyyy')
        : '-',
      plannedDownpaymentDate: item.plannedDownpaymentDate
        ? formatDate(item.plannedDownpaymentDate, 'dd/MM/yyyy')
        : '-',
      idleDays: item.idleDays || '-',
      categoryClass: item.categoryClass,
      nbv: item.nbv?.number ? `${item.nbv.number} €` : '-',
      carGr: item.carGr?.minPrice?.number
        ? `${item.carGr.minPrice.number} €`
        : '-',
      defleetPriority: priorityMap[item.defleetPriority]?.label || '-',
      monthOfDefleet: item.monthOfDefleet
        ? formatDate(item.monthOfDefleet, 'MM/yyyy')
        : '-',
      condition: item.condition || '-',
    };
  });
};

const getServicePointsOptions = (data, restOptions) => {
  const responseData = [...data].map((item) => {
    return {
      text: item?.name || item?.text || '',
      value: item?.id || item?.value || '',
    };
  });

  const allOptions = [...responseData, ...restOptions];

  const seen = new Set();

  const uniqueOptions = allOptions.filter((option) => {
    if (seen.has(option.value)) {
      return false;
    }

    seen.add(option.value);

    return true;
  });

  return uniqueOptions?.sort((a, b) => a.text.localeCompare(b.text));
};

const getProcurementStagesOptions = () => {
  const fields = {
    0: 'Created',
    1: 'Order',
    2: 'Customs',
    3: 'Payment',
    4: 'Dealer',
    5: 'Fleet',
    6: 'Defleeted',
    7: 'Cancelled',
  };

  return Object?.keys(fields)?.map?.((key) => {
    return {
      text: fields[key],
      value: Number(key),
    };
  });
};

export const handleEnums = (data = {}) => {
  if (!data || !Object.keys(data).length) {
    return {};
  }

  const defaultItem = {
    value: null,
    text: '-',
  };

  const statusesOptions = data?.statuses?.map((item) => ({
    value: item.id,
    text: AVAILABILITY_STATUSES?.[item?.name]?.text,
  }));
  const skuTypes = data?.skuTypes?.map((item) => ({
    value: item.id,
    text: item.name,
  }));

  const reservationsTypesOptions = data?.reservationsTypes?.map((item) => ({
    value: item.id,
    text: RESERVATIONS_TYPES?.[item.name]?.text,
  }));
  const bodyTypesOptions = data?.bodyTypes?.map((item) => ({
    value: item.id,
    text: item.name,
  }));

  const colors = Object.keys(data?.colors)?.map((item) => ({
    value: data?.colors[item].slug,
    text: data?.colors[item].name,
  }));

  const transmissions = data?.transmissions?.map((item) => ({
    value: item.id,
    text: item.name,
  }));

  const fuelTypes = data?.fuelTypes?.map((item) => ({
    value: item.id,
    text: item.name,
  }));

  const engineTypes = data?.engineTypes?.map((item) => ({
    value: item.id,
    text: item.name,
  }));

  const brandOptions = Object.keys(data?.models)?.map((key) => ({
    value: data.models[key].slug,
    text: data.models[key].name,
    models: Object.keys(data.models[key].models).map((modelKey) => ({
      value: data.models[key].models[modelKey].slug,
      text: `${data.models[key].models[modelKey].name} (${data.models[key].name})`,
    })),
  }));

  const servicePoints = getServicePointsOptions(data.servicePoints, [
    defaultItem,
    ...DEFAULT_LOCATIONS,
  ]);

  const procurementStages = getProcurementStagesOptions();

  const sourcingTypes = data?.sourcingTypes?.map((sourcingType) => ({
    value: sourcingType,
    text: sourcingType,
  }));

  const categoryClasses = Object.keys(data?.categoryClasses || {})?.map(
    (item) => ({
      value: data?.categoryClasses[item].slug,
      text: data?.categoryClasses[item].name,
    })
  );

  const conditions = data?.conditions?.map((item) => ({
    value: item.id,
    text: item.name,
  }));

  return {
    statuses: statusesOptions, // [defaultItem, ...statusesOptions],
    reservationsTypes: reservationsTypesOptions,
    bodyTypes: bodyTypesOptions,
    brands: brandOptions,
    servicePoints,
    skuTypes,
    colors,
    transmissions,
    fuelTypes,
    engineTypes,
    procurementStages,
    sourcingTypes,
    categoryClasses,
    conditions,
  };
};
